import React, { useEffect, useState, useRef } from 'react';
import { Pagination, PaginationItem, PaginationLink } from 'reactstrap';
import ProviderService from '../../services/providerservice';
import { showConfirm, showSuccessDetail, showDeleteDetail, showError, showLoading, hideLoading } from '../../components/utils/swal-helpers';

// ─────────────────────────────────────────────────────────
// Constants
// ─────────────────────────────────────────────────────────

const ROWS_PER_PAGE = 8;

/**
 * id_service_type = 2 คือ Banking Service
 * ใช้เป็น fixed value เพราะหน้านี้จัดการเฉพาะ bank
 */
const SERVICE_TYPE_BANKING = 2;

// ─────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────

/** คำนวณ page numbers ที่แสดงรอบ current page (±2) */
const getPageNumbers = (currentPage, totalPages) => {
    const start = Math.max(1, currentPage - 2);
    const end   = Math.min(totalPages, currentPage + 2);
    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
};

// ─────────────────────────────────────────────────────────
// Modals
// ─────────────────────────────────────────────────────────

/** Modal เพิ่ม/แก้ไข Bank */
const BankModal = ({ mode, data, onClose, onSave }) => {
    const [form, setForm] = useState({
        name_th: data?.name_th || '',
        name_en: data?.name_en || '',
    });

    const isEdit = mode === 'edit';

    const handleChange = (field, value) =>
        setForm((prev) => ({ ...prev, [field]: value }));

    const handleSave = () => setTimeout(() => onSave(form), 0);

    return (
        <div className="mc-modal-overlay" onClick={onClose}>
            <div className="mc-modal" onClick={(e) => e.stopPropagation()}>
                <div className="mc-modal-header">
                    <h3>{isEdit ? 'แก้ไข Edit' : 'เพิ่มธนาคารใหม่ New Bank'}</h3>
                </div>
                <div className="mc-modal-body">
                    <div className="border-line">
                        <div className="mc-form-row">
                            <div className="mc-form-group mb-8">
                                <label>Name (TH)</label>
                                <input
                                    type="text"
                                    placeholder="Input"
                                    value={form.name_th}
                                    onChange={(e) => handleChange('name_th', e.target.value)}
                                />
                            </div>
                            <div className="mc-form-group">
                                <label>Name (EN)</label>
                                <input
                                    type="text"
                                    placeholder="Input"
                                    value={form.name_en}
                                    onChange={(e) => handleChange('name_en', e.target.value)}
                                />
                            </div>
                        </div>
                    </div>
                </div>
                <div className="mc-modal-footer">
                    <button className="mc-btn mc-btn--cancel" onClick={onClose}>Cancel</button>
                    <button className="mc-btn mc-btn--save" onClick={handleSave}>
                        {isEdit ? 'Save' : 'Create'}
                    </button>
                </div>
            </div>
        </div>
    );
};

/** Modal เพิ่ม/แก้ไข Branch ของ Bank */
const BranchModal = ({ mode, data, providerName, onClose, onSave }) => {
    const [form, setForm] = useState({
        name_th: data?.name_th || '',
        name_en: data?.name_en || '',
        map:     data?.map    || '',
    });

    const isEdit = mode === 'edit';

    const handleChange = (field, value) =>
        setForm((prev) => ({ ...prev, [field]: value }));

    const handleSave = () => setTimeout(() => onSave(form), 0);

    return (
        <div className="mc-modal-overlay" onClick={onClose}>
            <div className="mc-modal" onClick={(e) => e.stopPropagation()}>
                <div className="mc-modal-header">
                    <h3>{isEdit ? 'แก้ไขสาขา' : 'เพิ่มสาขาใหม่ New Branch'}</h3>
                </div>
                {providerName && (
                    <div className="mc-modal-bank-name">
                        <span className="mc-modal-bank-badge">{providerName}</span>
                    </div>
                )}
                <div className="mc-modal-body">
                    <div className="border-line">
                        <div className="mc-form-group mb-8">
                            <label>Name (TH)</label>
                            <input
                                type="text"
                                placeholder="Input"
                                value={form.name_th}
                                onChange={(e) => handleChange('name_th', e.target.value)}
                            />
                        </div>
                        <div className="mc-form-group">
                            <label>Map</label>
                            <input
                                type="text"
                                placeholder="Input"
                                value={form.map}
                                onChange={(e) => handleChange('map', e.target.value)}
                            />
                        </div>
                    </div>
                </div>
                <div className="mc-modal-footer">
                    <button className="mc-btn mc-btn--cancel" onClick={onClose}>Cancel</button>
                    <button className="mc-btn mc-btn--save" onClick={handleSave}>
                        {isEdit ? 'Save' : 'Create'}
                    </button>
                </div>
            </div>
        </div>
    );
};

// ─────────────────────────────────────────────────────────
// Main Component
// ─────────────────────────────────────────────────────────

const AdminBank = () => {
    // ── Data state ──
    const [banks, setBanks]       = useState([]);
    const [branches, setBranches] = useState({}); // { bankId: branch[] }
    const [expanded, setExpanded] = useState({}); // { bankId: boolean }

    // ── UI state ──
    const [search, setSearch]           = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [modal, setModal]             = useState(null); // null | { type, mode?, data?, providerId?, providerName? }
    const [newBankId, setNewBankId]     = useState(null); // highlight หลัง create bank
    const [newBranchId, setNewBranchId] = useState(null); // highlight หลัง create branch

    // ── Refs ──
    const highlightRef = useRef(null);

    // โหลดข้อมูลตอน mount และ clear timeout เมื่อ unmount
    useEffect(() => {
        loadBanks();
        return () => { if (highlightRef.current) clearTimeout(highlightRef.current); };
    }, []);

    // ─────────────────────────────────────────────────────
    // Helpers
    // ─────────────────────────────────────────────────────

    /** highlight row ที่เพิ่งสร้าง 10 วิ แล้วล้าง */
    const startHighlight = (setter, id) => {
        setter(id);
        if (highlightRef.current) clearTimeout(highlightRef.current);
        highlightRef.current = setTimeout(() => setter(null), 10000);
    };

    // ─────────────────────────────────────────────────────
    // Data loaders
    // ─────────────────────────────────────────────────────

    const loadBanks = async () => {
        try {
            const resp = await ProviderService.SearchProvider({
                id_service_type: SERVICE_TYPE_BANKING,
                status: 1,
            });
            if (!resp.data.is_success || !resp.data.result) return;

            // sort A-Z ตามชื่อภาษาอังกฤษ
            const sorted = [...resp.data.result].sort((a, b) =>
                (a.name_en || '').localeCompare(b.name_en || '', 'en')
            );
            setBanks(sorted);

            // โหลด branches ทั้งหมดพร้อมกัน (parallel)
            const branchResponses = await Promise.all(
                sorted.map((bank) =>
                    ProviderService.SearchProviderBranch({ id_provider: bank.id, status: 1 })
                )
            );
            const branchMap = {};
            sorted.forEach((bank, i) => {
                branchMap[bank.id] = branchResponses[i].data.result || [];
            });
            setBranches(branchMap);
        } catch (err) { console.error(err); }
    };

    /** โหลด branches ของ bank เดียว (ใช้หลัง add/edit/delete branch) */
    const loadBranches = async (providerId) => {
        try {
            const resp = await ProviderService.SearchProviderBranch({ id_provider: providerId, status: 1 });
            if (!resp.data.is_success || !resp.data.result) return;
            setBranches((prev) => ({ ...prev, [providerId]: resp.data.result }));
        } catch (err) { console.error(err); }
    };

    /** toggle expand/collapse row พร้อม lazy load branches */
    const toggleExpand = (id) => {
        setExpanded((prev) => {
            const isOpening = !prev[id];
            if (isOpening && !branches[id]) loadBranches(id);
            return { ...prev, [id]: isOpening };
        });
    };

    // ─────────────────────────────────────────────────────
    // Handlers — Bank
    // ─────────────────────────────────────────────────────

    const handleSaveBank = async (form) => {
        const { mode: currentMode, data: currentData } = modal;
        setModal(null);

        const confirmed = await showConfirm({
            title: currentMode === 'edit' ? 'ยืนยันการแก้ไขธนาคาร' : 'ยืนยันการเพิ่มธนาคารใหม่',
            detail: 'กด Confirm เพื่อดำเนินการต่อ',
        });
        if (!confirmed.isConfirmed) return;

        try {
            showLoading();
            const payload = {
                name_th:             form.name_th,
                name_en:             form.name_en || form.name_th,
                id_service_type:     SERVICE_TYPE_BANKING,
                id_sub_service_type: 0,
                status:              1,
            };

            let createdId = null;
            if (currentMode === 'edit') {
                await ProviderService.UpdateProvider(currentData?.id, payload);
            } else {
                const resp = await ProviderService.CreateProvider(payload);
                createdId = resp.data?.result?.id || resp.data?.result?.[0]?.id || null;
            }

            hideLoading();
            await showSuccessDetail({
                icon: 'mdi mdi-check-circle',
                title: currentMode === 'edit' ? 'แก้ไขธนาคารสำเร็จแล้ว' : 'เพิ่มธนาคารสำเร็จแล้ว',
            });

            await loadBanks();
            if (currentMode === 'add' && createdId) startHighlight(setNewBankId, createdId);
        } catch (err) { hideLoading(); showError(); }
    };

    const handleDeleteBank = async (bank) => {
        const confirmed = await showConfirm({
            title: 'แน่ใจใช่ไหมว่าต้องการลบธนาคาร',
            detail: 'กด Confirm เพื่อดำเนินการต่อ',
        });
        if (!confirmed.isConfirmed) return;
        try {
            showLoading();
            await ProviderService.DeleteProvider(bank.id);
            hideLoading();
            await showDeleteDetail({ title: 'ลบธนาคารออกแล้ว' });
            loadBanks();
        } catch (err) { hideLoading(); showError(); }
    };

    // ─────────────────────────────────────────────────────
    // Handlers — Branch
    // ─────────────────────────────────────────────────────

    const handleSaveBranch = async (form) => {
        const { mode: currentMode, data: currentData, providerId } = modal;
        setModal(null);

        const confirmed = await showConfirm({
            title: currentMode === 'edit' ? 'ยืนยันการแก้ไขสาขา' : 'ยืนยันการเพิ่มสาขาใหม่',
            detail: 'กด Confirm เพื่อดำเนินการต่อ',
        });
        if (!confirmed.isConfirmed) return;

        try {
            showLoading();
            const payload = {
                name_th:             form.name_th,
                name_en:             form.name_en || form.name_th,
                map:                 form.map || '',
                id_provider:         providerId,
                id_service_type:     SERVICE_TYPE_BANKING,
                id_sub_service_type: 0,
                status:              1,
            };

            let createdId = null;
            if (currentMode === 'edit') {
                await ProviderService.UpdateProviderBranch(currentData?.id, payload);
            } else {
                const resp = await ProviderService.CreateProviderBranch(payload);
                createdId = resp.data?.result?.id || resp.data?.result?.[0]?.id || null;
            }

            hideLoading();
            await showSuccessDetail({
                icon: 'mdi mdi-check-circle',
                title: currentMode === 'edit' ? 'แก้ไขสาขาสำเร็จแล้ว' : 'เพิ่มสาขาสำเร็จแล้ว',
            });

            await loadBranches(providerId);
            setExpanded((prev) => ({ ...prev, [providerId]: true })); // เปิด expand หลัง save
            if (currentMode === 'add' && createdId) startHighlight(setNewBranchId, createdId);
        } catch (err) { hideLoading(); showError(); }
    };

    const handleDeleteBranch = async (branch, providerId) => {
        const confirmed = await showConfirm({
            title: 'แน่ใจใช่ไหมว่าต้องการลบสาขา',
            detail: 'กด Confirm เพื่อดำเนินการต่อ',
        });
        if (!confirmed.isConfirmed) return;
        try {
            showLoading();
            await ProviderService.DeleteProviderBranch(branch.id);
            hideLoading();
            await showDeleteDetail({ title: 'ลบสาขาออกแล้ว' });
            loadBranches(providerId);
        } catch (err) { hideLoading(); showError(); }
    };

    // ─────────────────────────────────────────────────────
    // Derived data (filter + pagination)
    // ─────────────────────────────────────────────────────

    const filtered = banks.filter((row) => {
        const q = search.toLowerCase();
        if (!q) return true;
        return (
            (row.name_th || '').toLowerCase().includes(q) ||
            (row.name_en || '').toLowerCase().includes(q)
        );
    });

    const totalPages  = Math.max(1, Math.ceil(filtered.length / ROWS_PER_PAGE));
    const safePage    = Math.min(currentPage, totalPages);
    const paged       = filtered.slice((safePage - 1) * ROWS_PER_PAGE, safePage * ROWS_PER_PAGE);
    const pageNumbers = getPageNumbers(safePage, totalPages);

    const goPage = (p) => {
        if (p >= 1 && p <= totalPages) setCurrentPage(p);
    };

    const handleSearchChange = (e) => {
        setSearch(e.target.value);
        setCurrentPage(1);
    };

    const countLabel = filtered.length === 0
        ? '0 รายการ'
        : `${(safePage - 1) * ROWS_PER_PAGE + 1}-${Math.min(safePage * ROWS_PER_PAGE, filtered.length)} จาก ${filtered.length} รายการ`;

    // ─────────────────────────────────────────────────────
    // Render
    // ─────────────────────────────────────────────────────

    return (
        <>
            <div className="mc-page">
                {/* ── Header ── */}
                <div className="mc-header">
                    <div className="mc-header__left">
                        <h2 className="mc-title">Master Data - Bank</h2>
                        <p className="mc-subtitle">รายชื่อธนาคาร</p>
                    </div>
                    <button
                        className="mc-btn mc-btn--new"
                        onClick={() => setModal({ mode: 'add', type: 'bank' })}
                    >
                        <i className="ri-add-line" /> New Bank
                    </button>
                </div>

                <div className="mc-card">
                    {/* ── Toolbar: search + count ── */}
                    <div className="mc-toolbar">
                        <div className="mc-search">
                            <i className="ri-search-line" />
                            <input
                                type="text"
                                placeholder="Select"
                                value={search}
                                onChange={handleSearchChange}
                            />
                        </div>
                        <div className="mc-toolbar__right">
                            <span className="mc-count">{countLabel}</span>
                        </div>
                    </div>

                    {/* ── Table ── */}
                    <div style={{ overflowX: 'auto' }}>
                        <table className="mc-table">
                            <thead>
                                <tr>
                                    <th style={{ width: 60 }}>No.</th>
                                    <th>ชื่อธนาคาร</th>
                                    <th>Bank Name</th>
                                    <th style={{ width: 80 }}>สาขา</th>
                                    <th style={{ width: 130 }}>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {paged.length === 0 ? (
                                    <tr>
                                        <td colSpan={5} className="mc-table-empty">ไม่มีข้อมูล</td>
                                    </tr>
                                ) : (
                                    paged.map((bank, idx) => (
                                        <React.Fragment key={bank.id}>
                                            {/* ── Bank row ── */}
                                            <tr className={bank.id === newBankId ? 'mc-row-new' : ''}>
                                                <td className="mc-col-no">
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                                        <button
                                                            className="mc-expand-btn"
                                                            onClick={() => toggleExpand(bank.id)}
                                                        >
                                                            <i className={expanded[bank.id] ? 'ri-arrow-down-s-fill' : 'ri-arrow-right-s-fill'} />
                                                        </button>
                                                        {(safePage - 1) * ROWS_PER_PAGE + idx + 1}
                                                    </div>
                                                </td>
                                                <td>{bank.name_th}</td>
                                                <td className="mc-col-muted">{bank.name_en || '-'}</td>
                                                <td style={{ textAlign: 'center' }}>
                                                    {branches[bank.id]?.length ?? '-'}
                                                </td>
                                                <td>
                                                    <div className="mc-actions">
                                                        <button
                                                            className="mc-action-btn mc-action-btn--edit"
                                                            onClick={() => setModal({ mode: 'edit', type: 'bank', data: bank })}
                                                        >
                                                            <i className="ri-pencil-line" />
                                                        </button>
                                                        <button
                                                            className="mc-action-btn mc-action-btn--delete"
                                                            onClick={() => handleDeleteBank(bank)}
                                                        >
                                                            <i className="ri-delete-bin-line" />
                                                        </button>
                                                        <button
                                                            className="mc-action-btn mc-action-btn--add"
                                                            onClick={() => setModal({
                                                                mode: 'add',
                                                                type: 'branch',
                                                                providerId:   bank.id,
                                                                providerName: bank.name_th,
                                                            })}
                                                        >
                                                            <i className="ri-add-line" />
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>

                                            {/* ── Branch rows (แสดงเมื่อ expand) ── */}
                                            {expanded[bank.id] && (branches[bank.id] || []).map((branch) => (
                                                <tr
                                                    key={branch.id}
                                                    className={`mc-branch-row${branch.id === newBranchId ? ' mc-row-new' : ''}`}
                                                >
                                                    <td />
                                                    <td className="mc-branch-name">
                                                        <i className="ri-corner-down-right-line" />
                                                        {branch.name_th}
                                                    </td>
                                                    <td>
                                                        {branch.map && (
                                                            <a
                                                                href={branch.map}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                className="mc-map-link"
                                                            >
                                                                ดูแผนที่
                                                            </a>
                                                        )}
                                                    </td>
                                                    <td />
                                                    <td>
                                                        <div className="mc-actions">
                                                            <button
                                                                className="mc-action-btn mc-action-btn--edit"
                                                                onClick={() => setModal({
                                                                    mode: 'edit',
                                                                    type: 'branch',
                                                                    data:         branch,
                                                                    providerId:   bank.id,
                                                                    providerName: bank.name_th,
                                                                })}
                                                            >
                                                                <i className="ri-pencil-line" />
                                                            </button>
                                                            <button
                                                                className="mc-action-btn mc-action-btn--delete"
                                                                onClick={() => handleDeleteBranch(branch, bank.id)}
                                                            >
                                                                <i className="ri-delete-bin-line" />
                                                            </button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))}
                                        </React.Fragment>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* ── Pagination ── */}
                    <div className="table-pagination">
                        <Pagination>
                            <PaginationItem disabled={safePage === 1}>
                                <PaginationLink first onClick={() => goPage(1)} />
                            </PaginationItem>
                            <PaginationItem disabled={safePage === 1}>
                                <PaginationLink previous onClick={() => goPage(safePage - 1)} />
                            </PaginationItem>
                            {pageNumbers.map((p) => (
                                <PaginationItem key={p} active={p === safePage}>
                                    <PaginationLink onClick={() => goPage(p)}>{p}</PaginationLink>
                                </PaginationItem>
                            ))}
                            <PaginationItem disabled={safePage === totalPages}>
                                <PaginationLink next onClick={() => goPage(safePage + 1)} />
                            </PaginationItem>
                            <PaginationItem disabled={safePage === totalPages}>
                                <PaginationLink last onClick={() => goPage(totalPages)} />
                            </PaginationItem>
                        </Pagination>
                    </div>
                </div>
            </div>

            {/* ── Modals ── */}
            {modal?.type === 'bank' && (
                <BankModal
                    mode={modal.mode}
                    data={modal.data}
                    onClose={() => setModal(null)}
                    onSave={handleSaveBank}
                />
            )}
            {modal?.type === 'branch' && (
                <BranchModal
                    mode={modal.mode}
                    data={modal.data}
                    providerName={modal.providerName}
                    onClose={() => setModal(null)}
                    onSave={handleSaveBranch}
                />
            )}
        </>
    );
};

export default AdminBank;